import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { blogs } from '../data/blogsData';
import { FAQitem } from '../utils/FAQitem';


const winterTips = [
    {
        id: 'tip-1',
        title: '1. Test the Battery Before Temperatures Drop',
        text:
            'Batteries always seem to give up at the worst time, and cold weather makes a weak one show its age fast. If the car has been slow to crank or the battery is already a few years old, winter is exactly when that starts mattering.'
    },
    {
        id: 'tip-2',
        title: '2. Inspect Tires and Tread Depth',
        text:
            'Good tires matter even more in winter because grip is already limited. If tread is getting low or the tires are worn unevenly, the car is going to feel a lot less confident on cold, wet, or slippery roads.'
    },
    {
        id: 'tip-3',
        title: '3. Check Tire Pressure Regularly',
        text:
            'Tire pressure drops as the temperature drops, so winter is when underinflated tires start showing up more often. That hurts grip, fuel economy, and how stable the car feels.'
    },
    {
        id: 'tip-4',
        title: '4. Top Up Winter-Safe Fluids',
        text:
            'Winter puts more pressure on your fluids, especially washer fluid. Make sure you are using something made for lower temperatures and not just whatever happened to be in the bottle last season.'
    },
    {
        id: 'tip-5',
        title: '5. Check Coolant Condition and Level',
        text:
            'Coolant matters in winter just as much as summer. If the level is low or the coolant is old, you are asking the engine to deal with cold weather without the protection it needs.'
    },
    {
        id: 'tip-6',
        title: '6. Inspect Wipers and Washer Fluid',
        text:
            'Winter visibility gets ugly fast with road spray, slush, rain, or frost. If your wipers are already streaking or chattering, replace them before the weather gives you a reason to regret waiting.'
    },
    {
        id: 'tip-7',
        title: '7. Test Lights and Defrosters',
        text:
            'Winter usually means darker mornings, earlier evenings, and fogged-up glass. Make sure the lights and defrosters are working properly before you need them in bad weather.'
    },
    {
        id: 'tip-8',
        title: '8. Inspect the Brakes',
        text:
            'If the brakes already squeak, grind, vibrate, or feel weak, winter is not the season to ignore it. Slippery roads already increase stopping distance, so you do not want the braking system adding more drama.'
    },
    {
        id: 'tip-9',
        title: '9. Make Sure the Heating System Works Properly',
        text:
            'A heater is not just about comfort. It helps clear the windshield and keeps the cabin from fogging up. If it is weak now, it is probably going to annoy you a lot more once the weather gets properly cold.'
    }
];

const faqs = [
    {
        question: 'How do you prepare your car for winter?',
        answer:
            'Start with the battery, tires, tire pressure, coolant, wipers, washer fluid, brakes, lights, heating system, and emergency supplies.'
    },
    {
        question: 'Does cold weather affect car batteries?',
        answer:
            'Yes. Cold weather reduces battery performance, which is why a weak battery often shows itself for the first time in winter.'
    },
    {
        question: 'Should tire pressure be checked more often in winter?',
        answer:
            'Yes. Pressure usually drops as temperatures fall, so winter is a good time to check it more regularly.'
    },
    {
        question: 'Why is windshield washer fluid important in winter?',
        answer:
            'Because winter roads throw spray, dirt, slush, and salt at the windshield constantly. Good washer fluid helps you keep visibility under control.'
    },
    {
        question: 'Can CarFido help me get my car winter-ready?',
        answer:
            'Yes. CarFido can help you understand seasonal maintenance needs, think through warning signs, and get ahead of winter problems.'
    }
];

const Blog7 = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const blogId = 7;
    const blogData = blogs.find(b => b.id === blogId);

    return (
        <div className="bg-white">
            <div className="max-w-[1180px] mx-auto px-5 py-5 text-left">
                <button
                    onClick={() => navigate('/blogs')}
                    className="inline-flex fixed top-30 bg-black p-1 rounded-full hover:scale-110 items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium mb-5"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                </button>
            </div>

            <div className="max-w-[1180px] mx-auto px-5 pb-[70px] flex flex-col lg:flex-row items-start gap-10">
                <main className="flex-1 max-w-full lg:max-w-[760px] min-w-0">
                    <section className="pb-6 border-b border-gray-200 mb-7">
                        <h1 className="text-3xl md:text-4xl lg:text-[42px] leading-[1.15] mb-3.5 text-gray-900 font-bold tracking-tight">
                            How to Prepare Your Car for Winter: Essential Cold-Weather Maintenance Tips
                        </h1>
                        <p className="text-lg md:text-xl text-gray-600 mb-[18px]">
                            Winter has a way of exposing every weak spot in a car. A battery that felt fine last week, worn tires you meant to replace, or washer fluid you forgot about can all become a problem the moment it gets cold.
                        </p>
                        <div className="text-sm text-gray-500">
                            By <strong className="text-gray-900">CarFido Editorial Team</strong>
                            &nbsp;|&nbsp; Updated March 2026 &nbsp;|&nbsp; 11 min read
                        </div>
                    </section>

                    <figure className="mb-8">
                        <img
                            src="/blogs/7_1.jpeg"
                            alt="Car being prepared for winter weather"
                            title="How to Prepare Your Car for Winter"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Featured image: winter_car_preparation_featured.png
                        </figcaption>
                    </figure>

                    <nav className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-8">
                        <div className="text-base font-bold text-gray-900 mb-2.5">In this article</div>
                        <ul className="m-0 pl-[18px] text-gray-700 list-disc space-y-1">
                            <li><a href="#quick-answer" className="text-gray-900 no-underline hover:underline">Quick answer</a></li>
                            <li><a href="#winter-table" className="text-gray-900 no-underline hover:underline">Winter car care summary</a></li>
                            <li><a href="#why-it-matters" className="text-gray-900 no-underline hover:underline">Why winter prep matters</a></li>
                            <li><a href="#tip-1" className="text-gray-900 no-underline hover:underline">Battery</a></li>
                            <li><a href="#tip-2" className="text-gray-900 no-underline hover:underline">Tires</a></li>
                            <li><a href="#tip-3" className="text-gray-900 no-underline hover:underline">Tire pressure</a></li>
                            <li><a href="#tip-4" className="text-gray-900 no-underline hover:underline">Fluids</a></li>
                            <li><a href="#tip-5" className="text-gray-900 no-underline hover:underline">Coolant</a></li>
                            <li><a href="#tip-6" className="text-gray-900 no-underline hover:underline">Wipers</a></li>
                            <li><a href="#tip-7" className="text-gray-900 no-underline hover:underline">Lights and defrosters</a></li>
                            <li><a href="#tip-8" className="text-gray-900 no-underline hover:underline">Brakes</a></li>
                            <li><a href="#tip-9" className="text-gray-900 no-underline hover:underline">Heating system</a></li>
                            <li><a href="#tip-10" className="text-gray-900 no-underline hover:underline">Emergency kit</a></li>
                            <li><a href="#dash-section" className="text-gray-900 no-underline hover:underline">Stay winter-ready with CarFido</a></li>
                            <li><a href="#faq" className="text-gray-900 no-underline hover:underline">FAQ</a></li>
                        </ul>
                    </nav>

                    <section className="mb-10 overflow-hidden">
                        <p className="mb-[18px] text-gray-700">
                            Winter driving gets harder the second the temperature drops, and the car feels every bit of it. That is why the little things you can ignore in mild weather suddenly matter a lot more.
                        </p>
                        <p className="mb-[18px] text-gray-700">
                            Batteries crank slower, tire pressure drops, visibility gets worse, and weak cooling or heating systems start showing their age fast. A little prep before winter settles in saves a lot of stress later.
                        </p>
                        <p className="mb-0 text-gray-700">
                            This guide covers the practical checks that make cold-weather driving easier, safer, and a lot less annoying.
                        </p>
                    </section>

                    <section id="quick-answer" className="mb-9 bg-gray-50 border-l-4 border-gray-900 rounded-lg p-[18px]">
                        <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2">Quick Answer</div>
                        <p className="m-0 text-gray-900 font-medium">
                            <strong>How do you prepare your car for winter?</strong><br />
                            Start with the battery, tires, tire pressure, winter-safe fluids, coolant, wipers, lights, defrosters, brakes, heating system, and a small emergency kit. The goal is simple: remove weak spots before cold weather exposes them.
                        </p>
                    </section>

                    <section id="winter-table" className="mb-10">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">Winter Car Care Summary</h2>
                        <div className="overflow-x-auto border border-gray-200 rounded-xl">
                            <table className="w-full border-collapse bg-white text-sm">
                                <thead className="bg-gray-50 text-gray-900 font-bold">
                                    <tr>
                                        <th className="text-left py-3.5 px-4 border-b border-gray-200">Area</th>
                                        <th className="text-left py-3.5 px-4 border-b border-gray-200">Why It Matters</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Battery and charging</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Cold weather exposes weak batteries quickly</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Tires and pressure</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Grip and stability matter more on cold, wet, and slippery roads</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Fluids, wipers, lights</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Winter visibility and cold-weather reliability depend on them</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 font-medium text-gray-900">Brakes, heater, emergency kit</td>
                                        <td className="py-3.5 px-4 text-gray-600">Helps you stay safe and deal with roadside delays more easily</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section id="why-it-matters" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">Why Winter Prep Matters</h2>
                        <p className="mb-4 text-gray-700">
                            Winter does not create every problem from scratch. A lot of the time it just exposes the weak spots that were already there.
                        </p>
                        <p className="mb-5 text-gray-700">
                            The battery that was getting tired, the tires that were already worn, or the wipers that were only just acceptable can all turn into real problems once mornings get colder and roads get worse.
                        </p>
                        <ul className="pl-6 m-0 space-y-2 text-gray-700 list-disc">
                            <li>Cold weather makes weak batteries show themselves fast</li>
                            <li>Low temperatures drop tire pressure and reduce grip</li>
                            <li>Poor visibility becomes a bigger problem in winter</li>
                            <li>Small maintenance issues become more annoying and more urgent</li>
                        </ul>
                    </section>

                    <figure className="mb-10">
                        <img
                            src="/blogs/7_2.jpeg"
                            alt="Checking battery and tires before winter weather"
                            title="Battery and tire checks before winter"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Inline image: winter_battery_tire_check.png
                        </figcaption>
                    </figure>

                    {winterTips.map((tip) => (
                        <section key={tip.id} id={tip.id} className="mb-11">
                            <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                                {tip.title}
                            </h2>
                            <p className="mb-0 text-gray-700">{tip.text}</p>
                        </section>
                    ))}

                    <figure className="mb-10">
                        <img
                            src="/blogs/7_3.jpeg"
                            alt="Checking windshield wipers and defroster for winter"
                            title="Winter visibility checks"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Inline image: winter_wiper_defroster_check.png
                        </figcaption>
                    </figure>

                    <section id="tip-10" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            10. Pack a Basic Winter Emergency Kit
                        </h2>
                        <p className="mb-4 text-gray-700">
                            Even if the car is in good shape, winter weather can still leave you waiting longer than expected. A small emergency kit makes those situations a lot easier to deal with.
                        </p>
                        <ul className="pl-6 m-0 space-y-2 text-gray-700 list-disc">
                            <li>Flashlight</li>
                            <li>Phone charger or power bank</li>
                            <li>Warm gloves or extra clothing</li>
                            <li>Water and a few snacks</li>
                            <li>Ice scraper</li>
                            <li>First-aid kit</li>
                            <li>Basic roadside tools if you normally carry them</li>
                        </ul>
                    </section>

                    <section id="dash-section" className="mb-10 overflow-hidden">
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                            <h2 className="text-2xl leading-tight mb-3.5 text-gray-900 font-bold">
                                Stay Winter-Ready with CarFido
                            </h2>
                            <p className="mb-3.5 text-gray-700">
                                Winter maintenance gets easier when you know what deserves attention before the weather turns on you.
                            </p>
                            <p className="mb-3.5 text-gray-700">
                                CarFido can help you think through symptoms, maintenance priorities, and the little warning signs that matter more once it gets cold.
                            </p>
                            <p className="mb-2.5 text-gray-900 font-bold uppercase tracking-wide text-sm">Drivers can ask questions such as:</p>
                            <ul className="pl-6 m-0 list-disc space-y-2 text-gray-700">
                                <li>What should I check on my car before winter starts?</li>
                                <li>Is my battery or tire condition likely to be a winter problem?</li>
                            </ul>
                        </div>
                    </section>

                    <section id="faq" className="mb-11 border-t border-gray-200 pt-8">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-5 text-gray-900 font-bold">
                            Frequently Asked Questions
                        </h2>
                        <div className="space-y-1">
                            {faqs.map((faq) => (
                                <FAQitem key={faq.question} question={faq.question} answer={faq.answer} />
                            ))}
                        </div>
                    </section>

                    <section id="final-thoughts" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            Final Thoughts
                        </h2>
                        <p className="mb-4 text-gray-700">
                            Winter prep does not have to be complicated. Usually it is just a matter of handling the basics before cold weather starts exposing problems for you.
                        </p>
                        <p className="mb-4 text-gray-700">
                            Batteries, tires, visibility, and fluids deserve the most attention because those are the areas most likely to make winter driving more stressful.
                        </p>
                        <p className="mb-0 text-gray-700">
                            Before winter settles in, give the car a proper once-over. A little effort now can save you a lot of hassle later.
                        </p>
                    </section>
                </main>

                <aside className="w-full lg:w-[300px] lg:flex-[0_0_300px] lg:sticky lg:top-5 self-start space-y-5">
                    <div className="border border-gray-200 rounded-xl bg-white p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-3">Quick Navigation</div>
                        <ul className="list-none p-0 m-0 space-y-2.5">
                            <li><a href="#quick-answer" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Quick answer</a></li>
                            <li><a href="#winter-table" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Winter summary</a></li>
                            <li><a href="#why-it-matters" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Why it matters</a></li>
                            <li><a href="#tip-1" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Battery check</a></li>
                            <li><a href="#tip-2" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Tires & Grip</a></li>
                            <li><a href="#tip-6" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Wipers & Visibility</a></li>
                            <li><a href="#tip-8" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Brake safety</a></li>
                            <li><a href="#faq" className="no-underline text-gray-600 hover:text-gray-900 text-sm">FAQ</a></li>
                        </ul>
                    </div>

                    <div className="border border-gray-200 rounded-xl bg-gray-50 p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-2.5">Related Reads</div>
                        {(() => {
                            const otherBlogs = blogs.filter(b => b.id !== blogId);
                            const startIndex = blogId % otherBlogs.length;
                            return [...otherBlogs.slice(startIndex), ...otherBlogs.slice(0, startIndex)].slice(0, 3);
                        })().map((relatedBlog) => (
                            <Link
                                key={relatedBlog.id}
                                to={relatedBlog.path}
                                className="block no-underline text-gray-900 mb-3 text-sm leading-snug font-medium hover:text-gray-600 transition-colors"
                            >
                                {relatedBlog.title}
                            </Link>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default Blog7;
